// src/pages/messages/TeamChatRedirect.jsx
//
// Smart redirector for /teams/:teamId/chat. Looks up the team's
// auto-created team_announcement channel and bounces the user to
// /messages/:chatId. Lets the rest of the app link to a stable
// URL without knowing the chat id.
//
// Implementation note: we resolve the team's channel by reading
// the user's full chat list (via useChats) and finding the one
// matching teamId client-side. Why not a direct getDocs query on
// `chats where type==team_announcement and teamId==X`? Because
// Firestore rules reject that query: the rule says "must be a
// participant" but the query doesn't filter by participantIds, so
// Firestore can't statically prove every result would be readable.
// Filtering by participantIds + then by teamId client-side keeps
// the query rule-safe AND reuses the inbox subscription, so this
// redirect is usually instant for users who've already loaded
// Messages this session.
//
// Used by:
//   - Team detail page "Message Team" button
//   - Dashboard "Team Chat" header button
//   - Inbox empty state link

import React, { useEffect, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { useRole } from '@/context/RoleContext'
import useChats from '@/hooks/useChats'
import {
  ArrowPathIcon,
  ChatBubbleLeftRightIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline'
import TeamSubNav from '@/components/team/TeamSubNav'

const HEAD_FONT = "'Barlow Condensed', sans-serif"

// Race condition: when a coach creates a NEW team, the
// onTeamCreated_createAnnouncementChannel cloud function fires
// async to create the channel. If the coach taps "Team Chat"
// within 1-2 seconds, useChats hasn't seen the new channel yet
// and we'd show "not available." Retry the lookup a few times
// before showing the failure state.
const RETRY_INTERVAL_MS = 1500
const MAX_RETRIES = 4 // ~6 seconds total wait window

export default function TeamChatRedirect() {
  const { teamId } = useParams()
  const { firebaseUser } = useAuth()
  const { teamRole } = useRole()
  const uid = firebaseUser?.uid || null
  const { chats, loading } = useChats(uid)
  const [retryCount, setRetryCount] = useState(0)
  const [givenUp, setGivenUp] = useState(false)
  // Role-aware help text — a parent who can't access the chat
  // doesn't need a "contact Kurt" instruction (that's coach-side
  // self-service support copy). Parents are told to ask their
  // team's coach instead, since the coach is the one who can
  // re-issue the invite or trigger the membership refresh.
  const role = teamRole(teamId)

  // Find this team's channel from the user's accessible chats
  const channel = chats.find(
    (c) => c.type === 'team_announcement' && c.teamId === teamId
  )

  // Auto-retry: if loading is done but channel isn't found yet,
  // wait a tick and retry. The useChats subscription will pick
  // up newly-created channels via onSnapshot, so we don't need
  // to manually re-query. We just bump retryCount to keep the
  // "still trying" UI visible for up to MAX_RETRIES * INTERVAL.
  useEffect(() => {
    if (loading || channel) return
    if (retryCount >= MAX_RETRIES) {
      setGivenUp(true)
      return
    }
    const t = setTimeout(() => setRetryCount((n) => n + 1), RETRY_INTERVAL_MS)
    return () => clearTimeout(t)
  }, [loading, channel, retryCount])

  if (channel) {
    return <Navigate to={`/messages/${channel.id}`} replace />
  }

  // Loading initial chat list, OR retrying for a freshly-created
  // channel: show a "setting up" state instead of the not-found
  // screen. Most channel-creation races resolve within 1-3 seconds.
  if (loading || (!channel && !givenUp)) {
    return (
      <div className="min-h-screen bg-[#f7faf5]">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-10">
          {/* Team sub-nav: keeps Overview/Roster/Schedule/Chat tabs visible while drilled into team-scoped content. */}
          <TeamSubNav teamId={teamId} />
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <ArrowPathIcon className="h-8 w-8 text-[#5a8a1f] animate-spin mb-4" />
            <p
              className="text-sm font-bold uppercase tracking-wide text-[#5a8a1f]"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
            >
              Setting up your team chat...
            </p>
            {retryCount > 1 && (
              <p className="text-xs text-zinc-400 mt-2">
                Just a moment, your channel is being created.
              </p>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Gave up after retries. Either the channel really doesn't
  // exist for this team, or the user isn't a participant. Both
  // produce the same UX: friendly explanation + escape hatch.
  return (
    <div className="min-h-screen bg-[#f7faf5]">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-10">
        {/* Team sub-nav: keeps Overview/Roster/Schedule/Chat tabs visible while drilled into team-scoped content. */}
        <TeamSubNav teamId={teamId} />
        <div className="max-w-md mx-auto text-center pt-6">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 mb-5">
          <ChatBubbleLeftRightIcon className="h-8 w-8 text-[#5a8a1f]" />
        </div>
        <h2
          className="text-xl font-bold uppercase text-[#0d1f14] mb-2"
          style={{ fontFamily: HEAD_FONT }}
        >
          Team chat not available
        </h2>
        <p className="text-sm text-zinc-500 mb-2 max-w-sm mx-auto">
          {role === 'parent'
            ? "You don't have access to this team's chat yet. Your team's coach can add you — they may need to re-send your invite or refresh your membership."
            : "You don't have access to this team's chat. This usually means you were added to the team after the channel was created, or you're not a member of this team."}
        </p>
        <p className="text-xs text-zinc-400 mb-6">
          {role === 'parent'
            ? "Need help? Tap the bug icon to message support."
            : role === 'coach'
            ? "Coach: contact support via the bug button to refresh your channel membership."
            : "If you should be on this team, ask the coach who invited you to refresh your access."}
        </p>
        <Link
          to="/messages"
          className="inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2 text-sm font-bold uppercase transition-all active:scale-[0.97]"
          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Go to Messages
        </Link>
        </div>
      </div>
    </div>
  )
}
